import EmptyState from '../DataDisplay/EmptyState';
import ErrorState from '../DataDisplay/ErrorState';

/**
 * A read-only table for server-paginated, server-sorted data.
 *
 * Deliberately not FullTable: its search filters the client-side array, which
 * on a paginated result searches only the page you can see while looking like a
 * global search. Row and hover classes are copied from it so the two match.
 */
export default function AnalyticsTable({
  columns,
  rows,
  rowKey = (row, index) => row.key ?? row.uuid ?? index,
  loading = false,
  error = '',
  emptyTitle = 'Nothing to show',
  emptyMessage = 'No AI spend was recorded for this period and filter.',
  skeletonRows = 6,
  onRowClick,
  sort,
  onSortChange,
  footer,
}) {
  if (error) return <ErrorState message={error} />;

  const sortableHeader = (column) => {
    if (!column.sortKey || !onSortChange) return column.label;
    const descending = `-${column.sortKey}`;
    const isActive = sort === column.sortKey || sort === descending;
    const next = sort === descending ? column.sortKey : descending;
    return (
      <button
        type="button"
        onClick={() => onSortChange(next)}
        className="inline-flex items-center gap-1 font-semibold hover:text-slate-900 dark:hover:text-white"
      >
        {column.label}
        <span aria-hidden="true" className={isActive ? 'opacity-100' : 'opacity-30'}>
          {sort === column.sortKey ? '↑' : '↓'}
        </span>
      </button>
    );
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
      {/* Wide tables scroll inside their own container; the page never does. */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
          <thead className="bg-slate-50 dark:bg-slate-900/40">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={`whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 ${
                    column.headerClassName || ''
                  }`}
                >
                  {sortableHeader(column)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {loading &&
              Array.from({ length: skeletonRows }).map((_, index) => (
                <tr key={`skeleton-${index}`}>
                  {columns.map((column) => (
                    <td key={column.key} className="px-4 py-3">
                      <div className="h-4 w-full animate-pulse rounded bg-slate-100 dark:bg-slate-700" />
                    </td>
                  ))}
                </tr>
              ))}
            {!loading &&
              rows.map((row, index) => (
                <tr
                  key={rowKey(row, index)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={
                    onRowClick
                      ? 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50'
                      : ''
                  }
                >
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={`whitespace-nowrap px-4 py-3 text-sm text-slate-700 dark:text-slate-200 ${
                        column.className || ''
                      }`}
                    >
                      {column.render ? column.render(row, index) : row[column.key]}
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {!loading && rows.length === 0 && (
        <EmptyState title={emptyTitle} message={emptyMessage} />
      )}
      {footer}
    </div>
  );
}
